import type { JSX } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useProject, useRuntime } from '../../context';
import { formatBytes, formatDuration, formatKbps, orDash } from '../../format';
import { formatEncoder } from '../../health/healthModel';
import { useLiveDuration } from '../../hooks/useTicker';
import '../panels.css';

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function RenderStatsSection(): JSX.Element {
  const render = useRuntime((r) => r.render);
  if (!render) return <div className="ui-dim">The renderer has not reported yet.</div>;
  return (
    <dl className="stats-grid">
      <Stat label="FPS" value={`${render.fps.toFixed(1)} / ${render.targetFps}`} />
      <Stat label="Frame time (avg)" value={`${render.frameTimeAvgMs.toFixed(2)} ms`} />
      <Stat label="Frame time (max)" value={`${render.frameTimeMaxMs.toFixed(2)} ms`} />
      <Stat label="Rendered frames" value={String(render.renderedFrames)} />
      <Stat label="Skipped frames" value={String(render.skippedFrames)} />
      <Stat label="Late frames" value={String(render.lateFrames)} />
      <Stat label="Dropped output frames" value={String(render.droppedOutputFrames)} />
      <Stat label="Output" value={`${render.outputWidth}×${render.outputHeight}`} />
      <Stat label="Render scale" value={`${Math.round(render.renderScale * 100)}%${render.degraded ? ' (degraded)' : ''}`} />
      <Stat label="Backend" value={render.backend === 'webgpu' ? 'WebGPU' : 'WebGL 2'} />
    </dl>
  );
}

function RecordingSection(): JSX.Element {
  const recording = useRuntime((r) => r.recording);
  const duration = useLiveDuration(recording.durationMs, recording.status === 'recording');
  return (
    <dl className="stats-grid">
      <Stat label="Status" value={recording.status} />
      <Stat label="Duration" value={formatDuration(duration)} />
      <Stat label="Size" value={formatBytes(recording.bytesWritten)} />
      <Stat label="Format" value={orDash(recording.mimeType)} />
    </dl>
  );
}

function SwarmSection(): JSX.Element {
  const swarm = useRuntime((r) => r.swarm);
  const stats = swarm.stats;
  if (swarm.status === 'idle' || !stats) return <div className="ui-dim">Not seeding a swarm.</div>;
  return (
    <dl className="stats-grid">
      <Stat label="Status" value={swarm.status} />
      <Stat label="Viewers" value={String(stats.viewers)} />
      <Stat label="First-hop peers" value={String(stats.firstHopPeers)} />
      <Stat label="Encoded A/V" value={stats.encodedKbps == null ? '—' : formatKbps(stats.encodedKbps)} />
      <Stat label="P2P upload" value={formatKbps(stats.uploadKbps)} />
      <Stat label="Uploaded" value={formatBytes(stats.bytesUploaded)} />
      <Stat label="Live segment" value={stats.liveSeq === null ? '—' : String(stats.liveSeq)} />
      <Stat label="Segments published" value={String(stats.segmentsPublished)} />
      <Stat label="Encoder queue" value={String(stats.encoderQueue)} />
      <Stat label="Dropped frames" value={String(stats.droppedFrames)} />
      <Stat label="Dropped segments" value={String(stats.droppedSegments)} />
      <Stat label="Codecs" value={orDash(stats.codecs)} />
      {stats.compat ? (
        <>
          <Stat label="H.264 fallback" value={stats.compat.reason ? `${stats.compat.state}: ${stats.compat.reason}` : stats.compat.state} />
          <Stat label="Fallback encoder" value={stats.compat.width ? `${stats.compat.width}×${stats.compat.height} ${orDash(stats.compat.encoder)}` : '—'} />
          <Stat label="Fallback A/V" value={stats.compat.kbps == null ? '—' : formatKbps(stats.compat.kbps)} />
          <Stat label="Fallback viewers" value={String(stats.compat.viewers)} />
        </>
      ) : null}
    </dl>
  );
}

function NetworkSection(): JSX.Element {
  const network = useRuntime((r) => r.network);
  const names = useRuntime(useShallow((r) => Object.fromEntries(r.broadcast.peers.map((p) => [p.peerId, p.name || p.role]))));
  const ids = Object.keys(network);
  if (ids.length === 0) return <div className="ui-dim">No active connections.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="ui-table">
        <thead>
          <tr>
            <th scope="col">Peer</th>
            <th scope="col">State</th>
            <th scope="col">Route</th>
            <th scope="col">Codecs</th>
            <th scope="col">Encoder</th>
            <th scope="col">Out</th>
            <th scope="col">In</th>
            <th scope="col">Available</th>
            <th scope="col">RTT</th>
            <th scope="col">Loss</th>
            <th scope="col">Video</th>
            <th scope="col">Limited by</th>
            <th scope="col">NACK / PLI</th>
          </tr>
        </thead>
        <tbody>
          {ids.map((id) => {
            const s = network[id]!;
            return (
              <tr key={id}>
                <th scope="row">{id === 'whip' ? 'WHIP' : (names[id] ?? id.slice(0, 8))}</th>
                <td>{s.connectionState}</td>
                <td>{orDash(s.candidateType)}</td>
                <td>{[s.videoCodec, s.audioCodec].filter(Boolean).join(' + ') || '—'}</td>
                <td>{formatEncoder(s.encoderImplementation, s.powerEfficientEncoder)}</td>
                <td className="ui-mono">{formatKbps(s.outKbps)}</td>
                <td className="ui-mono">{formatKbps(s.inKbps)}</td>
                <td className="ui-mono">{formatKbps(s.availableOutKbps)}</td>
                <td className="ui-mono">{s.rttMs === null ? '—' : `${Math.round(s.rttMs)} ms`}</td>
                <td className="ui-mono">{s.packetLossPct === null ? '—' : `${s.packetLossPct.toFixed(1)}%`}</td>
                <td className="ui-mono">
                  {s.frameWidth && s.frameHeight ? `${s.frameWidth}×${s.frameHeight}` : '—'}
                  {s.fps === null ? '' : ` @ ${Math.round(s.fps)}`}
                </td>
                <td>{orDash(s.qualityLimitationReason)}</td>
                <td className="ui-mono">
                  {s.nackCount} / {s.pliCount}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function StatsPanel(): JSX.Element {
  const swarmMode = useProject((p) => p.output.streaming.mode === 'swarm');
  return (
    <div className="ui-panel">
      <div className="ui-panel-body stats">
        <section aria-label="Rendering">
          <div className="ui-section-title">Rendering</div>
          <RenderStatsSection />
        </section>
        <section aria-label="Recording">
          <div className="ui-section-title">Recording</div>
          <RecordingSection />
        </section>
        {swarmMode ? (
          <section aria-label="Swarm">
            <div className="ui-section-title">Swarm</div>
            <SwarmSection />
          </section>
        ) : null}
        <section aria-label="Network">
          <div className="ui-section-title">Network</div>
          <NetworkSection />
        </section>
      </div>
    </div>
  );
}
